import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Drawer from "@mui/material/Drawer";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import type React from "react";
import { useCallback, useMemo } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import type { ReplyTrigger } from "@/hooks/dispatch/dispatchTracking";
import { useComposer } from "@/hooks/dispatch/useDispatchComposer";
import { DISPATCH_PANEL_ATTRIBUTE } from "@/hooks/dispatch/useDispatchShortcuts";
import { useDriverDialNumber } from "@/hooks/dispatch/useDriverDialNumber";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { store } from "@/store/store";
import type { DriverRow, TagDefinition } from "@/types/driver";
import { zinc } from "@/ui/tokens";
import ContextPanel from "./context/ContextPanel";
import { unitNumber } from "./context/contextFormat";
import DispatchCallButton from "./DispatchCallButton";
import DispatchComposer, { DISPATCH_PANEL_COMPOSER_INPUT_ID } from "./DispatchComposer";
import { DISPATCH_COLOR_VALUES } from "./dispatchColors";
import type { DispatchPanelHandle } from "./panelScroll";
import DispatchThread from "./thread/DispatchThread";

/** The `?panel=` values the popover route accepts. */
export type DispatchPanel = "history" | "context";

/**
 * The right-hand History/Context overlay for one driver. It floats over the table (the table keeps its width) without a
 * backdrop, so the table stays live underneath: a click on another row selects that driver, and the page moves the
 * popover to them. The header is one row, so the panel's height goes to what it shows: a Back button, the driver's name
 * with the unit number of their truck in the dispatch purple, a Call button when the driver has a dialable number, and
 * the tabs that switch between the thread and the context sections. The History tab is `DispatchThread`, the page's own
 * view of the channel, so nothing here touches the driver list's channel components. One composer sits at the foot
 * under either tab, so a draft begun on Context is still there on History; the `reply` key puts the cursor in it while
 * the popover is open. Escape and Back hand control to the page, which owns the URL. Closed, it leaves the DOM.
 */
export default function DispatchPopover({
  open,
  panel,
  feedId,
  driver,
  tags,
  phoneTagSlug,
  workspaceId,
  now,
  onClose,
  onPanelChange,
  onSent,
  panelRef,
}: {
  open: boolean;
  panel: DispatchPanel;
  feedId: string | undefined;
  driver: DriverRow | null;
  tags: readonly TagDefinition[];
  /** The tag holding drivers' phone numbers, the Call button's last source; `null` reads no tag. */
  phoneTagSlug: string | null;
  workspaceId: string;
  /** A minute clock, for the thread's acknowledgement lookback. */
  now: number;
  onClose: () => void;
  onPanelChange: (panel: DispatchPanel) => void;
  /** What follows a reply from the popover's composer, as it stood when Send was pressed, told how it was pressed. */
  onSent?: (trigger: ReplyTrigger) => void;
  /** Receives the showing tab's handle, History's or Context's, so the page keys can page it. */
  panelRef?: React.Ref<DispatchPanelHandle>;
}) {
  if (!open) return null;

  const unit = driver ? unitNumber(driver, tags) : null;

  return (
    <Drawer
      anchor="right"
      // Persistent, not temporary: no modal, so no backdrop swallows the click that selects the next driver, no focus
      // trap fights the table, and the page's keyboard layer keeps Escape. Mounted only while open, so `appear` is
      // what slides it in.
      variant="persistent"
      open
      slotProps={{
        transition: { appear: true },
        paper: {
          "data-sb": TestIds.dispatch.popover,
          [DISPATCH_PANEL_ATTRIBUTE]: "",
          sx: {
            width: 640,
            maxWidth: "92vw",
            bgcolor: zinc[900],
            color: "white",
            borderLeft: `1px solid ${zinc[700]}`,
            display: "flex",
            flexDirection: "column",
          },
        } as React.ComponentProps<typeof Drawer>["PaperProps"],
      }}
    >
      <Box
        data-sb={TestIds.dispatch.popoverHeader}
        sx={{
          flex: "none",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          pt: 1,
          borderBottom: `1px solid ${zinc[800]}`,
        }}
      >
        <ButtonBase
          aria-label="Back"
          onClick={onClose}
          sx={{
            flex: "none",
            gap: 0.5,
            px: 1,
            height: 28,
            borderRadius: 1,
            border: `1px solid ${zinc[700]}`,
            color: zinc[300],
            fontSize: "0.75rem",
            "&:hover": { color: "white", bgcolor: zinc[800] },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 14 }} />
          Back
        </ButtonBase>
        <Typography
          component="h2"
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {driver?.name ?? driver?.alias ?? "Driver"}
        </Typography>
        {unit && (
          <Typography
            component="span"
            data-sb={TestIds.dispatch.popoverUnit}
            aria-label={`Unit ${unit}`}
            sx={{ flex: "none", fontSize: "1rem", fontWeight: 600, color: DISPATCH_COLOR_VALUES.purple }}
          >
            {unit}
          </Typography>
        )}
        <HeaderCallButton driver={driver} tags={tags} phoneTagSlug={phoneTagSlug} />
        <Tabs
          value={panel}
          onChange={(_event, value: DispatchPanel) => onPanelChange(value)}
          sx={{
            flex: "none",
            minHeight: 36,
            "& .MuiTab-root": { minHeight: 36, color: zinc[400], fontSize: "0.8125rem" },
          }}
        >
          <Tab value="history" label="History" data-sb={TestIds.dispatch.popoverTab} />
          <Tab value="context" label="Context" data-sb={TestIds.dispatch.popoverTab} />
        </Tabs>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {panel === "history" && feedId ? (
          <DispatchThread feedId={feedId} driver={driver} workspaceId={workspaceId} now={now} handleRef={panelRef} />
        ) : null}
        {panel === "context" ? (
          driver ? (
            <ContextPanel driver={driver} tags={tags} workspaceId={workspaceId} now={now} handleRef={panelRef} />
          ) : (
            <Typography sx={{ p: 2, color: zinc[400] }}>This driver is not in the current roster.</Typography>
          )
        ) : null}
      </Box>
      {feedId && (
        <Box sx={{ flex: "none", px: 2, py: 1.5, borderTop: `1px solid ${zinc[800]}`, bgcolor: zinc[900] }}>
          <ThreadComposer feedId={feedId} workspaceId={workspaceId} canReply={driver != null} onSent={onSent} />
        </Box>
      )}
    </Drawer>
  );
}

/**
 * The header's Call button on the driver's dialable number, its own component so the store reads behind the number
 * run only while the popover is open.
 */
function HeaderCallButton({
  driver,
  tags,
  phoneTagSlug,
}: {
  driver: DriverRow | null;
  tags: readonly TagDefinition[];
  phoneTagSlug: string | null;
}) {
  return <DispatchCallButton number={useDriverDialNumber(driver, tags, phoneTagSlug)} />;
}

/**
 * The popover's composer and its voice recorder, one instance under both tabs so switching tabs keeps the draft, and
 * apart from the tab bodies so a recording's per-frame timer re-renders the composer alone. The draft is kept per
 * channel, so it also follows the popover from driver to driver.
 */
function ThreadComposer({
  feedId,
  workspaceId,
  canReply,
  onSent,
}: {
  feedId: string;
  workspaceId: string;
  /** False while the popover has no driver to write to. */
  canReply: boolean;
  onSent?: (trigger: ReplyTrigger) => void;
}) {
  const spanAttributes = useMemo(() => ({ source: "dispatch", grain: "thread" }), []);
  const composer = useComposer({ key: feedId, feedId, workspaceId, spanAttributes, onSent });
  const isSilent = useCallback(() => store.get("feed", feedId)?.isSilent === true, [feedId]);
  const recorder = useVoiceRecorder({ workspaceId, feedId, isSilent, onStopped: composer.focus });
  return (
    <DispatchComposer
      composer={composer}
      feedId={canReply ? feedId : null}
      variant="thread"
      inputId={DISPATCH_PANEL_COMPOSER_INPUT_ID}
      testId={TestIds.dispatch.threadComposer}
      sendTestId={TestIds.dispatch.threadSend}
      recorder={recorder}
    />
  );
}
